import type { GroupChannel } from "@sendbird/chat/groupChannel";
import type { BaseMessage, FileMessage } from "@sendbird/chat/message";
import { useGroupChannelContext } from "@sendbird/uikit-react/GroupChannel/context";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChatEntityFeatureFlags } from "@/components/shared/chat-feature-flags-context";
import { isEntityFlagGated } from "@/components/shared/chat-message-content-renderer";
import type { ChatRenderContext } from "@/components/shared/chat-message-types";
import { useBatchSignedMedia } from "@/lib/hooks/use-signed-media";
import { getSendbirdMessageSenderDisplayName } from "@/lib/redacted-display";
import { normalizeConversationMembers } from "@/lib/sendbird/conversation-members";
import type {
  AttentionMessage,
  AttentionQuote,
  AttentionReadState,
} from "./attention-message";
import {
  type AttentionAdapterContext,
  getStableMessageId,
  orderMessagesForAttention,
  toAttentionMessages,
} from "./attention-message-adapter";
import { resolveSendableQuote } from "./attention-quote";
import {
  buildAttentionView,
  type AttentionReaction,
  type AttentionView,
} from "./attention-view";

type ChannelContext = ReturnType<typeof useGroupChannelContext>;
type ChannelMessage = ChannelContext["messages"][number];
type SendableMessage = Parameters<ChannelContext["toggleReaction"]>[0];

interface SendbirdReactionLike {
  key: string;
  userIds: string[];
}

interface MessageSenderLike {
  userId?: string;
  profileUrl?: string;
}

interface MessageExtras {
  reqId?: string;
  sender?: MessageSenderLike;
  sendingStatus?: string;
  reactions?: SendbirdReactionLike[];
}

function getMessageExtras(
  message: ChannelMessage | BaseMessage,
): MessageExtras {
  return message as MessageExtras;
}

function getMessageReqId(message: ChannelMessage | BaseMessage): string | null {
  const reqId = getMessageExtras(message).reqId;
  return reqId && reqId.length > 0 ? reqId : null;
}

function getMessageSender(
  message: ChannelMessage | BaseMessage,
): MessageSenderLike | undefined {
  return getMessageExtras(message).sender;
}

function getMessageSendingStatus(
  message: ChannelMessage | BaseMessage,
): string | undefined {
  return getMessageExtras(message).sendingStatus;
}

function getMessageReactions(message: ChannelMessage): SendbirdReactionLike[] {
  return getMessageExtras(message).reactions ?? [];
}

function toSignedUrlMap(
  rawUrls: string[],
  signedBatch: { url?: string }[],
): Map<string, string> {
  const map = new Map<string, string>();
  rawUrls.forEach((rawUrl, index) => {
    const signed = signedBatch[index]?.url;
    if (signed) map.set(rawUrl, signed);
  });
  return map;
}

export interface UseAttentionChannelResult {
  view: AttentionView;
  renderContext: ChatRenderContext;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  resolveQuote: (messageId: string) => AttentionQuote | null;
  onToggleReaction: (messageId: string, emoji: string) => void;
}

function getMessageUrl(message: ChannelMessage): string | null {
  return message.isFileMessage() ? message.url : null;
}

function getReactions(
  message: ChannelMessage,
  currentUserId: string,
  emojiUrlByKey: Map<string, string>,
): AttentionReaction[] {
  return getMessageReactions(message)
    .filter((reaction) => reaction.userIds.length > 0)
    .map((reaction) => ({
      emoji: reaction.key,
      count: reaction.userIds.length,
      reactedBySelf: reaction.userIds.includes(currentUserId),
      url: emojiUrlByKey.get(reaction.key) ?? null,
    }));
}

export function useAttentionChannel(
  emojiUrlByKey: Map<string, string>,
): UseAttentionChannelResult {
  const store = useSendbirdStateContext();
  const currentUserId = store.config.userId;
  const entityFlags = useChatEntityFeatureFlags();

  const {
    messages,
    loading,
    initialized,
    fetchChannelError,
    hasPrevious,
    loadPrevious,
    currentChannel,
    toggleReaction,
  } = useGroupChannelContext();

  const isGroup = (currentChannel?.memberCount ?? 0) > 2;

  const renderContext = useMemo<ChatRenderContext>(
    () => ({
      surface: "mentor",
      conversation: {
        id: currentChannel?.url ?? null,
        members: normalizeConversationMembers(currentChannel?.members),
      },
    }),
    [currentChannel],
  );

  const messagesById = useMemo(() => {
    const map = new Map<string, ChannelMessage>();
    for (const message of messages) {
      map.set(getStableMessageId(message), message);
    }
    return map;
  }, [messages]);

  const mediaUrls = useMemo(
    () =>
      messages
        .map(getMessageUrl)
        .filter((url): url is string => url !== null && url.length > 0),
    [messages],
  );

  const signedMedia = useBatchSignedMedia(mediaUrls);
  const signedMediaByUrl = useMemo(
    () => toSignedUrlMap(mediaUrls, signedMedia),
    [mediaUrls, signedMedia],
  );

  const senderAvatarUrls = useMemo(() => {
    if (!isGroup) return [];
    const urls = new Set<string>();
    for (const message of messages) {
      const profileUrl = getMessageSender(message)?.profileUrl;
      if (profileUrl && profileUrl.length > 0) urls.add(profileUrl);
    }
    return [...urls];
  }, [isGroup, messages]);

  const signedAvatars = useBatchSignedMedia(senderAvatarUrls);
  const signedAvatarByUrl = useMemo(
    () => toSignedUrlMap(senderAvatarUrls, signedAvatars),
    [senderAvatarUrls, signedAvatars],
  );

  const localFileUrlsRef = useRef<Map<string, string>>(new Map());

  // GroupChannel folds optimistic locals into `messages`; track pending/failed
  // by sendingStatus so local object URLs can be revoked after confirm/fail.
  const pendingReqIds = useMemo(() => {
    const set = new Set<string>();
    for (const message of messages) {
      const status = getMessageSendingStatus(message);
      if (status !== "pending" && status !== "failed") continue;
      const reqId = getMessageReqId(message);
      if (reqId) set.add(reqId);
    }
    return set;
  }, [messages]);

  const resolveLocalFileUrl = useCallback(
    (message: FileMessage): string | null => {
      const reqId = getMessageReqId(message);
      if (!reqId) return null;
      const cache = localFileUrlsRef.current;
      const existing = cache.get(reqId);
      if (existing) return existing;
      const file = message.messageParams?.file;
      if (!(file instanceof Blob)) return null;
      const url = URL.createObjectURL(file);
      cache.set(reqId, url);
      return url;
    },
    [],
  );

  useEffect(() => {
    const cache = localFileUrlsRef.current;
    for (const [reqId, url] of cache) {
      if (!pendingReqIds.has(reqId)) {
        URL.revokeObjectURL(url);
        cache.delete(reqId);
      }
    }
  }, [pendingReqIds]);

  useEffect(() => {
    const cache = localFileUrlsRef.current;
    return () => {
      for (const url of cache.values()) URL.revokeObjectURL(url);
      cache.clear();
    };
  }, []);

  const reactionsByMessageId = useMemo(() => {
    const record: Record<string, AttentionReaction[]> = {};
    for (const message of messages) {
      const reactions = getReactions(message, currentUserId, emojiUrlByKey);
      if (reactions.length > 0) {
        record[getStableMessageId(message)] = reactions;
      }
    }
    return record;
  }, [messages, currentUserId, emojiUrlByKey]);

  const adaptedMessages = useMemo(() => {
    const ctx: AttentionAdapterContext = {
      currentUserId,
      resolveSenderDisplayName: (sender) =>
        getSendbirdMessageSenderDisplayName({
          user_id: sender.userId,
          nickname: sender.nickname,
          metadata: { role: sender.metaData?.role },
        }),
      resolveSenderAvatarUrl: (sender) =>
        sender.profileUrl
          ? signedAvatarByUrl.get(sender.profileUrl) ?? sender.profileUrl
          : null,
      resolveMediaUrl: (message: FileMessage) => {
        const signed = signedMediaByUrl.get(message.url);
        if (signed) return { url: signed };
        if (message.url) return { url: message.url };
        const local = resolveLocalFileUrl(message);
        return { url: local, pending: local !== null };
      },
    };

    return toAttentionMessages(orderMessagesForAttention(messages), ctx);
  }, [
    messages,
    currentUserId,
    signedMediaByUrl,
    signedAvatarByUrl,
    resolveLocalFileUrl,
  ]);

  // Read state is the only channel-derived field. `currentChannel` is a new
  // object on every read receipt / unread-count update, so patch it in here
  // instead of re-running the whole adapter above.
  const attentionMessages = useMemo(
    () =>
      adaptedMessages.map((message) => {
        const raw = messagesById.get(message.id);
        const readState = raw
          ? resolveReadState(raw, currentUserId, currentChannel)
          : undefined;
        return readState === undefined || readState === message.readState
          ? message
          : { ...message, readState };
      }),
    [adaptedMessages, messagesById, currentUserId, currentChannel],
  );

  const visibleMessages = useMemo(
    () =>
      attentionMessages.filter((message) => {
        const { content } = message;
        if (
          content.type !== "entityShared" &&
          content.type !== "entityInteraction"
        ) {
          return true;
        }
        return !isEntityFlagGated(
          content.rawData ?? undefined,
          entityFlags.isMomentsEnabled,
          entityFlags.isSwapsEnabled,
        );
      }),
    [
      attentionMessages,
      entityFlags.isMomentsEnabled,
      entityFlags.isSwapsEnabled,
    ],
  );

  const view = useMemo(
    () =>
      buildAttentionView(visibleMessages, { reactionsByMessageId, isGroup }),
    [visibleMessages, reactionsByMessageId, isGroup],
  );

  const attentionById = useMemo(() => {
    const map = new Map<string, AttentionMessage>();
    for (const message of attentionMessages) {
      map.set(message.id, message);
    }
    return map;
  }, [attentionMessages]);

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);

  // GroupChannelManager writes collection helpers into the store after mount;
  // first paint still has the empty initial state without them.
  const canLoadPrevious = resolveHasPrevious(hasPrevious);

  const loadMore = useCallback(() => {
    if (loadingMoreRef.current || !resolveHasPrevious(hasPrevious)) return;
    if (typeof loadPrevious !== "function") return;
    loadingMoreRef.current = true;
    setIsLoadingMore(true);
    void loadPrevious().finally(() => {
      loadingMoreRef.current = false;
      setIsLoadingMore(false);
    });
  }, [loadPrevious, hasPrevious]);

  const resolveQuote = useCallback(
    (messageId: string): AttentionQuote | null => {
      const message = attentionById.get(messageId);
      if (!message || message.direction === "system") return null;
      return resolveSendableQuote(message);
    },
    [attentionById],
  );

  const onToggleReaction = useCallback(
    (messageId: string, emoji: string) => {
      const message = messagesById.get(messageId);
      if (!message || message.isAdminMessage()) return;
      const reacted = getReactions(message, currentUserId, emojiUrlByKey).some(
        (reaction) => reaction.emoji === emoji && reaction.reactedBySelf,
      );
      toggleReaction(message as SendableMessage, emoji, reacted);
    },
    [messagesById, toggleReaction, currentUserId, emojiUrlByKey],
  );

  return {
    view,
    renderContext,
    isLoading: loading || !initialized,
    isLoadingMore,
    error: fetchChannelError ? "Unable to load this conversation." : null,
    hasMore: canLoadPrevious,
    loadMore,
    resolveQuote,
    onToggleReaction,
  };
}

function resolveHasPrevious(hasPrevious: unknown): boolean {
  if (typeof hasPrevious === "function") {
    return Boolean((hasPrevious as () => boolean)());
  }
  return Boolean(hasPrevious);
}

function resolveReadState(
  message: BaseMessage,
  currentUserId: string,
  channel: GroupChannel | null,
): AttentionReadState | undefined {
  const senderId = getMessageSender(message)?.userId;
  if (senderId !== currentUserId) return undefined;
  if (getMessageSendingStatus(message) !== "succeeded") return "sent";
  const unread = channel?.getUnreadMemberCount?.(message) ?? 1;
  return unread === 0 ? "read" : "sent";
}
